import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User, Mail, Lock, Globe, Clock, Camera, Save, AlertTriangle, Trash2, CheckCircle, ChevronLeft, Shield, Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, Settings as SettingsIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SelectDropdown } from '../components/SelectDropdown';
import { SettingsPanels } from '../components/SettingsPanels';
const LANGUAGE_OPTIONS = [{
  value: 'en',
  label: 'English (US)'
}, {
  value: 'es',
  label: 'Español'
}, {
  value: 'fr',
  label: 'Français'
}, {
  value: 'de',
  label: 'Deutsch'
}, {
  value: 'zh',
  label: '中文 (Chinese)'
}, {
  value: 'ja',
  label: '日本語 (Japanese)'
}];
const TIMEZONE_OPTIONS = [{
  value: 'UTC-8',
  label: 'Pacific Time (US & Canada) (UTC-08:00)'
}, {
  value: 'UTC-7',
  label: 'Mountain Time (US & Canada) (UTC-07:00)'
}, {
  value: 'UTC-6',
  label: 'Central Time (US & Canada) (UTC-06:00)'
}, {
  value: 'UTC-5',
  label: 'Eastern Time (US & Canada) (UTC-05:00)'
}, {
  value: 'UTC+0',
  label: 'Greenwich Mean Time (UTC+00:00)'
}, {
  value: 'UTC+1',
  label: 'Central European Time (UTC+01:00)'
}, {
  value: 'UTC+8',
  label: 'China Standard Time (UTC+08:00)'
}, {
  value: 'UTC+9',
  label: 'Japan Standard Time (UTC+09:00)'
}];
export function ProfilePage() {
  // Personal Info State
  const [firstName, setFirstName] = useState('Linda');
  const [lastName, setLastName] = useState('Taylor');
  const [email] = useState('linda.taylor@example.com'); // Read-only
  const [avatar, setAvatar] = useState<string | null>(null);
  // Password State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Preferences State
  const [language, setLanguage] = useState('en');
  const [timezone, setTimezone] = useState('UTC-5');
  // UI State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // Open the Settings tab when arriving from a settings link
  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab && ['general', 'security', 'preferences', 'settings'].includes(requestedTab)) {
      setActiveTab(requestedTab);
    }
  }, [location.search]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  const handleSave = () => {
    setSaveStatus('saving');
    setTimeout(() => {
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }, 1000);
  };
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatar(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  return <div className="flex-1 bg-gray-50 min-h-screen" data-id="element-2172" data-mp="1lucssmm6t6rl">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200" data-id="element-2173" data-mp="1qnnyulj6djig">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2174" data-mp="t78hjzwi3w7d">
          <div className="flex items-center h-16" data-id="element-2175" data-mp="t6yt4gr3tx3s">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2176" data-mp="y50j7lmy6wor">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2177" data-mp="30tifssaqbub1">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2178" data-mp="2vuyosdha3sbt">
                <Home size={18} data-id="element-2179" data-mp="34uu3utdyg7h4" />
                <span className="hidden sm:inline" data-id="element-2180" data-mp="34tshhsrhnyk3">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2181" data-mp="3jiow68o1w9to">
                <BarChart3 size={18} data-id="element-2182" data-mp="3sjwj5ul49o8g" />
                <span className="hidden sm:inline" data-id="element-2183" data-mp="3sivafwrkpdtw">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2184" data-mp="3ojmf4ngvx7w8">
                <FileText size={18} data-id="element-2185" data-mp="1u4tm8w7dyv7z" />
                <span className="hidden sm:inline" data-id="element-2186" data-mp="1u5v7tum1303y">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2187" data-mp="1k1oak3r9knvt">
                <UserCog size={18} data-id="element-2188" data-mp="1ab23ixxm55oq" />
                <span className="hidden sm:inline" data-id="element-2189" data-mp="1aa0pcklaw32s">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2190" data-mp="10cm7qhnn588w">
              <Link data-phoebe-nav="/alerts" to="/alerts" aria-label="Notifications" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2191" data-mp="10bb6aoho4cky">
                <Bell size={20} data-id="element-2192" data-mp="rczieu86rsw7" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2193" data-mp="rdsff3hcf415"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2194" data-mp="wcvlmr927gap">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2195" data-mp="2lra6h0gyij0x">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2196" data-mp="2oawtnurnpr51">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2197" data-mp="2f9gs8citkzcy">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2198" data-mp="2abxcf86h4eiw" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2199" data-mp="37xl094ov5oit">
                    <div className="py-2" data-id="element-2200" data-mp="2y8kzpgupql2i">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2201" data-mp="2y7je4ig2mg6j">
                        <Users size={18} className="text-gray-600" data-id="element-2202" data-mp="3qovnnbvdrj5y" />
                        <span className="font-medium text-gray-900" data-id="element-2203" data-mp="3qq6rtcsqmsxl">
                          Profile
                        </span>
                      </Link>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2204" data-mp="3ta3h2t1rbynr">
                        <Home size={18} className="text-gray-600" data-id="element-2205" data-mp="2390tnyhenm3" />
                        <span className="font-medium text-gray-900" data-id="element-2206" data-mp="71252bg857jq">
                          Learner Hub
                        </span>
                      </button>
                      <Link to="/admin" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2207" data-mp="g31nn86w0jos">
                        <UserCog size={18} className="text-gray-600" data-id="element-2208" data-mp="l0aahm2zdbwd" />
                        <span className="font-medium text-gray-900" data-id="element-2209" data-mp="psbhotlakcfj">
                          Admin Hub
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2210" data-mp="zqr9kys57pq3"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2211" data-mp="zsluid3ezhu6">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-600" data-id="element-2212" data-mp="1p31ckkyts96m">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" data-id="element-2213" data-mp="1z18mijup5qz4"></path>
                          <polyline points="16 17 21 12 16 7" data-id="element-2214" data-mp="1yz4je44mgvzw"></polyline>
                          <line x1="21" y1="12" x2="9" y2="12" data-id="element-2215" data-mp="1u37503cx5zpr"></line>
                        </svg>
                        <span className="font-medium text-gray-900" data-id="element-2216" data-mp="16d33c1jaka1e">
                          Logout
                        </span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-2217" data-mp="1b9ti8ljd0zl8">
        <div data-phoebe-tour="profile" className="mb-8" data-id="element-2218" data-mp="nl0rr4libjy0">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-2219" data-mp="inig36g3uqmb">Profile</h1>
          <p className="text-gray-600" data-id="element-2220" data-mp="ilefoyhf09qs">
            Manage your account information and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-2221" data-mp="2z1ubno93vow1">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-3" data-id="element-2222" data-mp="2lafp57fzp5k8">
            <nav className="space-y-1 sticky top-24" data-id="element-2223" data-mp="2lbquvcszwndz">
              <button onClick={() => setActiveTab('general')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2224" data-mp="2pazomho1cl9j">
                <User size={18} data-id="element-2225" data-mp="3hxst9b4jq4ot" />
                General
              </button>
              <button onClick={() => setActiveTab('security')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'security' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2226" data-mp="34048e3ahaxjr">
                <Shield size={18} data-id="element-2227" data-mp="1xlcyxb7ai4e4" />
                Security
              </button>
              <button onClick={() => setActiveTab('preferences')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'preferences' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2228" data-mp="3e1zmf29n93d1">
                <Globe size={18} data-id="element-2229" data-mp="39bsyz5qvbowd" />
                Preferences
              </button>
              <button onClick={() => setActiveTab('settings')} className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-[#30718D]/10 text-[#30718D]' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-2230" data-mp="399ona1mq9llj">
                <SettingsIcon size={18} data-id="element-2231" data-mp="2lf5bavypvgc8" />
                Settings
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-6" data-id="element-2232" data-mp="jmqlzbtuf9ls">
            {/* General Section */}
            {activeTab === 'general' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2233" data-mp="om3h0juvbmzg">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2234" data-mp="tryzdlkhcyii">
                  <div className="p-6 border-b border-gray-200" data-id="element-2235" data-mp="17jdjxwu1d7ro">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2236" data-mp="12qu1omkelogj">
                      Personal Information
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2237" data-mp="1he5fyw7yzmkc">
                      Update your photo and personal details.
                    </p>
                  </div>

                  <div className="p-6 space-y-8" data-id="element-2238" data-mp="1qfn0qrwbocfd">
                    {/* Avatar Upload */}
                    <div className="flex items-center gap-6" data-id="element-2239" data-mp="1mg4ik3vvyfg8">
                      <div className="relative group" data-id="element-2240" data-mp="3s81ldyq93uzh">
                        <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md" data-id="element-2241" data-mp="3s8jn3avon1es">
                          {avatar ? <img src={avatar} alt="Profile" className="w-full h-full object-cover" data-id="element-2242" data-mp="1r6ljc650v066" /> : <span className="text-2xl font-bold text-gray-500" data-id="element-2243" data-mp="1w4wpa5s74qj4">
                              LT
                            </span>}
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" data-id="element-2244" data-mp="2136qoy4iknd6">
                            <Camera className="text-white" size={24} data-id="element-2245" data-mp="2a5fyvhcb84ne" />
                          </div>
                        </div>
                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2246" data-mp="2f26ek3k7cy8a" />
                      </div>
                      <div data-id="element-2247" data-mp="2os94tvzxvy2x">
                        <h3 className="font-medium text-gray-900" data-id="element-2248" data-mp="2ovn610wva18s">
                          Profile Photo
                        </h3>
                        <p className="text-sm text-gray-500 mb-3" data-id="element-2249" data-mp="2xtqt41ni362p">
                          This will be displayed on your profile.
                        </p>
                        <div className="flex gap-3" data-id="element-2250" data-mp="m4sn74wao73n">
                          <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] relative" data-id="element-2251" data-mp="i51zja22e73c">
                            Change
                            <input type="file" accept="image/*" onChange={handleAvatarChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" data-id="element-2252" data-mp="d8bd84zo2f9h" />
                          </button>
                          <button onClick={() => setAvatar(null)} className="text-sm font-medium text-red-600 hover:text-red-700" data-id="element-2253" data-mp="15wyzo3w5jaom">
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2254" data-mp="r5gmseyl9w9h">
                      <div data-id="element-2255" data-mp="1omfd1rsxqxmg">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2256" data-mp="1omow0vfkc4in">
                          First Name
                        </label>
                        <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2257" data-mp="14ce76ilnqx0b" />
                      </div>
                      <div data-id="element-2258" data-mp="zcrk7hg122eb">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2259" data-mp="ze2zadexawkq">
                          Last Name
                        </label>
                        <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" data-id="element-2260" data-mp="1s1yqx3pmxyan" />
                      </div>
                    </div>

                    <div data-id="element-2261" data-mp="261q5p6nz7ipp">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2262" data-mp="260f1j5qmc8y2">
                        Email Address
                      </label>
                      <div className="relative" data-id="element-2263" data-mp="3villkopilab3">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2264" data-mp="3vhak4vjjken5" />
                        <input type="email" value={email} readOnly className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-500 cursor-not-allowed" data-id="element-2265" data-mp="3qhxyge4epdm5" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-2266" data-mp="46tomqbhbp2u">
                        Contact support to change your email address.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2267" data-mp="3lkxse37k4av1">
                    <button onClick={handleSave} disabled={saveStatus === 'saving'} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm disabled:opacity-70" data-id="element-2268" data-mp="3glum6ffubylh">
                      {saveStatus === 'saving' ? <>Saving...</> : saveStatus === 'success' ? <>
                          <CheckCircle size={18} data-id="element-2269" data-mp="3pmtqse8519g3" />
                          Saved
                        </> : <>
                          <Save size={18} data-id="element-2270" data-mp="3ul544yxn6232" />
                          Save Changes
                        </>}
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Security Section */}
            {activeTab === 'security' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2271" data-mp="36y5ay6oihub6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2272" data-mp="3fwha4x3g20f8">
                  <div className="p-6 border-b border-gray-200" data-id="element-2273" data-mp="3kqlh3gcpm8gb">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2274" data-mp="2n837u55bohxv">
                      Password & Security
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2275" data-mp="2s64s2tehinax">
                      Manage your password and account security.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2276" data-mp="2fq55cklk6745">
                    <div data-id="element-2277" data-mp="2aq91clcv2fol">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2278" data-mp="2aphfi292fwbi">
                        Current Password
                      </label>
                      <div className="relative" data-id="element-2279" data-mp="21pubd8oyccer">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2280" data-mp="21qma2sduiq99" />
                        <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Enter current password" data-id="element-2281" data-mp="1wxjdschop2pd" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2282" data-mp="1e1b3gu9te1n7">
                      <div data-id="element-2283" data-mp="31wfzzj4ks0gr">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2284" data-mp="31y97t1qxcqqc">
                          New Password
                        </label>
                        <div className="relative" data-id="element-2285" data-mp="2swjgrx0yftt3">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2286" data-mp="2svq5qjuyqi55" />
                          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Min. 8 characters" data-id="element-2287" data-mp="2ww1sc26vysj9" />
                        </div>
                      </div>
                      <div data-id="element-2288" data-mp="2e8et0en1os3g">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2289" data-mp="2e7n39kg4pol3">
                          Confirm New Password
                        </label>
                        <div className="relative" data-id="element-2290" data-mp="255nkonpgucg1">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-2291" data-mp="1lkvng0t7wq1k" />
                          <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:border-transparent transition-shadow" placeholder="Re-enter new password" data-id="element-2292" data-mp="1gm27m1qgkmy8" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100" data-id="element-2293" data-mp="1zl6fh769kaob">
                      <h4 className="text-sm font-medium text-blue-900 mb-2" data-id="element-2294" data-mp="2e9b1ol4iz4bu">
                        Password Requirements
                      </h4>
                      <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside" data-id="element-2295" data-mp="29azpi3qrchqg">
                        <li data-id="element-2296" data-mp="2nb0oterdjh3t">Minimum 8 characters long</li>
                        <li data-id="element-2297" data-mp="2ibfp324dr4q1">At least one uppercase letter</li>
                        <li data-id="element-2298" data-mp="2i930w7813y4j">At least one number</li>
                        <li data-id="element-2299" data-mp="2ut4gj9bli0tk">At least one special character</li>
                      </ul>
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2300" data-mp="2qs130th0sgvi">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2301" data-mp="34rah9nlo10wu">
                      Update Password
                    </button>
                  </div>
                </div>

                {/* Danger Zone */}
                <div className="bg-white rounded-xl shadow-sm border border-red-200 overflow-hidden" data-id="element-2302" data-mp="2lsxx76t7u6mh">
                  <div className="p-6 border-b border-red-100 bg-red-50" data-id="element-2303" data-mp="2h2r9d96j2q5a">
                    <h2 className="text-lg font-bold text-red-900 flex items-center gap-2" data-id="element-2304" data-mp="2h0oas0rb7ooy">
                      <AlertTriangle size={20} data-id="element-2305" data-mp="1tatxji32vy46" />
                      Danger Zone
                    </h2>
                    <p className="text-sm text-red-700 mt-1" data-id="element-2306" data-mp="1obzgakkrehm6">
                      Irreversible actions for your account.
                    </p>
                  </div>

                  <div className="p-6 flex items-center justify-between flex-wrap gap-4" data-id="element-2307" data-mp="8o2lwbjf80g6">
                    <div data-id="element-2308" data-mp="3r1sfvou5wiyi">
                      <h3 className="font-medium text-gray-900" data-id="element-2309" data-mp="3qyeccd9ri3ph">
                        Delete Account
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-2310" data-mp="mmivca8o4520">
                        Permanently delete your account and all of your content.
                      </p>
                    </div>
                    <button onClick={() => setShowDeleteModal(true)} className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors font-medium" data-id="element-2311" data-mp="dlupi2tfhb07">
                      Delete Account
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Preferences Section */}
            {activeTab === 'preferences' && <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }} className="space-y-6" data-id="element-2312" data-mp="18uoi8g2028z2">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200" data-id="element-2313" data-mp="z3tkofjazmyb">
                  <div className="p-6 border-b border-gray-200" data-id="element-2314" data-mp="v3t4oqgk33p5">
                    <h2 className="text-lg font-bold text-gray-900" data-id="element-2315" data-mp="v1gpum63h9gb">
                      Preferences
                    </h2>
                    <p className="text-sm text-gray-500 mt-1" data-id="element-2316" data-mp="l4kfsc0jsf9n">
                      Customize your platform experience.
                    </p>
                  </div>

                  <div className="p-6 space-y-6" data-id="element-2317" data-mp="g7u0vs0hbppt">
                    <div data-id="element-2318" data-mp="c5gwp81drzv5">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2319" data-mp="c68ffi9vq340">
                        Language
                      </label>
                      <SelectDropdown fullWidth ariaLabel="Language" value={language} onChange={setLanguage} options={LANGUAGE_OPTIONS} icon={<Globe size={18} data-id="element-2321" data-mp="3of0fn1ap7lmy" />} data-id="element-2320" data-mp="26hx266mcr9yy" />
                    </div>

                    <div data-id="element-2322" data-mp="3teweyn8gaow6">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2323" data-mp="3te4nnolvzdbp">
                        Time Zone
                      </label>
                      <SelectDropdown fullWidth scrollMenu ariaLabel="Time zone" value={timezone} onChange={setTimezone} options={TIMEZONE_OPTIONS} icon={<Clock size={18} data-id="element-2325" data-mp="35hz6d4a42zd7" />} data-id="element-2324" data-mp="22uvjb8kxemz" />
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-2326" data-mp="3afsc5w7i5rcy">
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium shadow-sm" data-id="element-2327" data-mp="3ehduvy6pwzv2">
                      Save Preferences
                    </button>
                  </div>
                </div>
              </motion.div>}

            {/* Settings Section */}
            {activeTab === 'settings' && <SettingsPanels data-id="element-2328" data-mp="x92f5kem3p1i" />}
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <AnimatePresence data-id="element-2329" data-mp="sb10dbo7ybvu">
        {showDeleteModal && <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={() => setShowDeleteModal(false)} data-id="element-2330" data-mp="scbz2x2t4tg3">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2331" data-mp="1285kz72f1zaj">
              <div className="flex items-center gap-3 mb-4" data-id="element-2332" data-mp="1l07uzzkjyhbt">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center" data-id="element-2333" data-mp="1yyxt9ij3yn1r">
                  <Trash2 size={24} className="text-red-600" data-id="element-2334" data-mp="1tzv0awgek7a5" />
                </div>
                <div data-id="element-2335" data-mp="1ty0ab3q9xoz7">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-2336" data-mp="2dlwj592bh9ed">
                    Delete Account?
                  </h2>
                  <p className="text-sm text-gray-500" data-id="element-2337" data-mp="2ijoiup97dzt4">
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-100 rounded-lg p-4 mb-6" data-id="element-2338" data-mp="28ma36rv3tf1v">
                <p className="text-sm text-red-800" data-id="element-2339" data-mp="28oeevvz8vicp">
                  Are you sure you want to delete your account? All of your
                  data, including course progress and certificates, will be
                  permanently removed.
                </p>
              </div>

              <div className="flex gap-3" data-id="element-2340" data-mp="1kuwceqclprj0">
                <button onClick={() => setShowDeleteModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-2341" data-mp="1pviuztmurup2">
                  Cancel
                </button>
                <button onClick={() => {
              // Handle delete logic
              setShowDeleteModal(false);
              alert('Account deletion request sent');
            }} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium shadow-sm" data-id="element-2342" data-mp="1guksjfs2bv8l">
                  Yes, Delete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}